import { useState, useEffect, useRef, useCallback } from 'react'
import type { AssessmentReportRecord } from '@really-assess/shared/integrations'
import { useAssessmentStore } from '../../store/assessmentStore'
import { getProctoringConfig } from '../../store/proctoringConfigStore'
import { fetchAssessmentReport } from '../../services/assessmentReport'
import { downloadAssessmentReportPdf } from '../../services/assessmentReportPdf'
import Badge from '../Badge'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'

export default function ResultsStep() {
  const {
    result,
    resetAssessment,
    sessionId,
    sessionToken,
    assessmentReport: storedReport,
    setAssessmentReport: setStoredReport,
  } = useAssessmentStore()
  const config = getProctoringConfig()

  const [showBadge, setShowBadge] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [isDownloadingReport, setIsDownloadingReport] = useState(false)
  const [assessmentReport, setAssessmentReport] = useState<AssessmentReportRecord | null>(storedReport)
  const [reportStatus, setReportStatus] = useState<'loading' | 'available' | 'unavailable'>(
    storedReport ? 'available' : 'loading'
  )
  const announcedRef = useRef(false)

  const downloadPDF = useCallback(async () => {
    const badgeElement = document.getElementById('badge-container')
    if (!badgeElement || !result) return

    setIsDownloading(true)

    try {
      const canvas = await html2canvas(badgeElement, {
        scale: 2,
        backgroundColor: '#1a1a2e',
        logging: false,
        useCORS: true,
      })

      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
      })

      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()

      const imgWidth = canvas.width
      const imgHeight = canvas.height
      const ratio = Math.min((pageWidth - 40) / imgWidth, (pageHeight - 40) / imgHeight)

      const finalWidth = imgWidth * ratio
      const finalHeight = imgHeight * ratio
      const x = (pageWidth - finalWidth) / 2
      const y = (pageHeight - finalHeight) / 2

      const imgData = canvas.toDataURL('image/png')
      pdf.addImage(imgData, 'PNG', x, y, finalWidth, finalHeight)

      const filename = `ReallyAssess_Certificate_${result.learnerName.replace(/\s+/g, '_')}_integrity.pdf`
      pdf.save(filename)
    } catch (error) {
      console.error('PDF download error:', error)
    } finally {
      setIsDownloading(false)
    }
  }, [result])

  const downloadReport = useCallback(() => {
    if (!result || !assessmentReport) return
    setIsDownloadingReport(true)
    try {
      downloadAssessmentReportPdf({
        assessmentTitle: result.assessmentTitle ?? 'Assessment',
        learnerName: result.learnerName,
        dateTaken: result.dateTaken.toISOString().split('T')[0],
        integrityScore: result.proctoringScore,
        passed: result.passed,
        report: assessmentReport,
      })
    } catch (error) {
      console.error('Assessment report PDF error:', error)
    } finally {
      setIsDownloadingReport(false)
    }
  }, [result, assessmentReport])

  useEffect(() => {
    if (!result || announcedRef.current) return
    announcedRef.current = true
    setShowBadge(true)
  }, [result])

  const loadAssessmentReport = useCallback(async () => {
    if (!sessionId || !sessionToken) {
      setReportStatus('unavailable')
      return
    }

    setReportStatus('loading')
    const data = await fetchAssessmentReport(sessionId, sessionToken)
    if (data?.available && data.report) {
      setAssessmentReport(data.report)
      setStoredReport(data.report)
      setReportStatus('available')
      return
    }

    setReportStatus('unavailable')
  }, [sessionId, sessionToken, setStoredReport])

  useEffect(() => {
    if (storedReport) {
      setAssessmentReport(storedReport)
      setReportStatus('available')
      return
    }

    if (!sessionId || !sessionToken) {
      setReportStatus('unavailable')
      return
    }

    let cancelled = false
    let attempts = 0
    const maxAttempts = 4

    const poll = async () => {
      attempts += 1
      const data = await fetchAssessmentReport(sessionId, sessionToken)
      if (cancelled) return

      if (data?.available && data.report) {
        setAssessmentReport(data.report)
        setStoredReport(data.report)
        setReportStatus('available')
        return
      }

      if (attempts >= maxAttempts) {
        setReportStatus('unavailable')
        return
      }

      window.setTimeout(poll, 2000)
    }

    setReportStatus('loading')
    void poll()

    return () => {
      cancelled = true
    }
  }, [sessionId, sessionToken, storedReport, setStoredReport])

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}m ${secs}s`
  }

  const getViolationSummary = () => {
    if (!result) return {}

    const summary: Record<string, number> = {}
    result.proctoringViolations.forEach((v) => {
      summary[v.type] = (summary[v.type] || 0) + 1
    })
    return summary
  }

  if (!result) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Calculating integrity score...</p>
        </div>
      </div>
    )
  }

  const violationSummary = getViolationSummary()

  return (
    <div className="flex-1 overflow-y-auto p-4 lg:p-6">
      <div className="max-w-2xl mx-auto space-y-5">
        {showBadge && result.passed && (
          <div className="animate-fade-in">
            <Badge
              learnerName={result.learnerName}
              assessmentTitle={result.assessmentTitle ?? 'Assessment'}
              dateTaken={result.dateTaken.toISOString().split('T')[0]}
              authenticationScore={result.proctoringScore}
              passed={result.passed}
            />
          </div>
        )}

        <div className="glass-card p-5">
          <h2 className="text-xl font-bold text-white mb-5 flex items-center gap-2">
            {result.passed ? (
              <>
                <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Integrity Verified
              </>
            ) : (
              <>
                <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {result.failureReason === 'time_limit_exceeded' ? 'Assessment Failed — Time Expired' : 'Assessment Not Passed'}
              </>
            )}
          </h2>

          <div className="bg-assess-bg rounded-lg p-6 mb-5 text-center">
            <p className="text-gray-400 text-sm mb-2">Integrity Score</p>
            <div className={`text-5xl font-bold mb-2 ${result.proctoringScore >= config.passingScore ? 'text-green-400' : 'text-red-400'}`}>
              {result.proctoringScore}%
            </div>
            <p className="text-gray-400 text-sm">
              {result.passed
                ? `Above the required ${config.passingScore}% threshold`
                : result.failureReason === 'time_limit_exceeded'
                  ? 'Assessment ended because the time limit was reached'
                  : result.failureReason === 'proctoring_suspended'
                    ? 'Assessment ended due to proctoring suspension'
                    : `Below the required ${config.passingScore}% threshold`}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="bg-assess-bg rounded-lg p-3 text-center">
              <div className="text-xl font-bold text-white">{result.learnerName}</div>
              <div className="text-xs text-gray-400">Learner</div>
            </div>
            <div className="bg-assess-bg rounded-lg p-3 text-center">
              <div className="text-xl font-bold text-white">{formatTime(result.timeSpent)}</div>
              <div className="text-xs text-gray-400">Time Monitored</div>
            </div>
            <div className="bg-assess-bg rounded-lg p-3 text-center col-span-2">
              <div className={`text-xl font-bold ${result.proctoringViolations.length === 0 ? 'text-green-400' : 'text-yellow-400'}`}>
                {result.proctoringViolations.length}
              </div>
              <div className="text-xs text-gray-400">Proctoring Violations</div>
            </div>
          </div>

          {result.proctoringViolations.length > 0 && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-5">
              <h4 className="text-yellow-400 font-medium mb-2 text-sm">Violation Breakdown</h4>
              <div className="space-y-1">
                {Object.entries(violationSummary).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">
                      {type === 'no_face' && 'Face not detected'}
                      {type === 'multiple_faces' && 'Multiple faces'}
                      {type === 'identity_mismatch' && 'Identity mismatch'}
                      {type === 'tab_switch' && 'Tab switched'}
                      {type === 'window_blur' && 'Window unfocused'}
                      {type === 'fullscreen_exit' && 'Fullscreen exited'}
                      {type === 'time_limit_exceeded' && 'Time limit exceeded'}
                    </span>
                    <span className="text-yellow-400">{count}x</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!result.passed && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-5">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-red-400 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  {result.failureReason === 'time_limit_exceeded' ? (
                    <>
                      <h4 className="text-red-400 font-medium text-sm">Time Limit Exceeded</h4>
                      <p className="text-xs text-gray-400 mt-1">
                        The allowed time for this assessment has ended. Your result is recorded as failed.
                      </p>
                    </>
                  ) : result.failureReason === 'proctoring_suspended' ? (
                    <>
                      <h4 className="text-red-400 font-medium text-sm">Assessment Suspended</h4>
                      <p className="text-xs text-gray-400 mt-1">
                        This assessment was ended due to repeated proctoring violations.
                      </p>
                    </>
                  ) : (
                    <>
                      <h4 className="text-red-400 font-medium text-sm">Integrity Threshold Not Met</h4>
                      <p className="text-xs text-gray-400 mt-1">
                        Your integrity score of {result.proctoringScore}% is below the required {config.passingScore}% threshold.
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-3">
            <div className="flex gap-3 flex-wrap">
              {result.passed && showBadge && (
                <button
                  onClick={downloadPDF}
                  disabled={isDownloading}
                  className="flex-1 min-w-[10rem] bg-primary-500 hover:bg-primary-600 disabled:bg-primary-500/50 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  {isDownloading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download Certificate
                    </>
                  )}
                </button>
              )}
              <button
                onClick={downloadReport}
                disabled={reportStatus !== 'available' || isDownloadingReport}
                className="flex-1 min-w-[10rem] bg-white/10 hover:bg-white/15 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm border border-white/10"
              >
                {isDownloadingReport ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Generating...
                  </>
                ) : reportStatus === 'loading' ? (
                  'Checking report…'
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Download Assessment Report
                  </>
                )}
              </button>
              <button
                onClick={resetAssessment}
                className="flex-1 min-w-[10rem] bg-gray-600 hover:bg-gray-700 text-white font-medium py-3 px-4 rounded-lg transition-colors text-sm"
              >
                {result.passed ? 'New Assessment' : 'Retake Assessment'}
              </button>
            </div>
            {reportStatus === 'unavailable' && (
              <p className="text-xs text-gray-500">
                Assessment report not available yet — finish the assessment in ReallyAvatar so the report sections can
                be submitted.{' '}
                <button
                  type="button"
                  onClick={() => void loadAssessmentReport()}
                  className="text-primary-400 hover:text-primary-300 underline underline-offset-2"
                >
                  Check again
                </button>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

